import {
  AppTypeEnum,
  useInstalledAppsListQuery,
  usePluginsQuery,
} from "@dashboard/graphql";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Box, Skeleton } from "@saleor/macaw-ui-next";
import { CreditCard } from "lucide-react";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { EmptyState } from "../../components/EmptyState";
import {
  AppIframeView,
  ExtensionItem,
  ExtensionList,
  PluginConfigView,
} from "../../components/ExtensionConfig";
import { StepWrapper } from "../../components/StepWrapper";
import { useWizardContext } from "../../context/WizardContext";
import { messages } from "./messages";

const PAYMENT_FORM_ID = "wizard-payment-form";

const SUPPORTED_PAYMENT_NAMES = ["paypal", "authorize.net", "affirm"];

const isSupportedPayment = (name: string | null | undefined): boolean => {
  if (!name) return false;

  const lower = name.toLowerCase();

  return SUPPORTED_PAYMENT_NAMES.some(supported => lower.includes(supported));
};

type SelectedExtension = {
  id: string;
  type: "app" | "plugin";
};

export const PaymentStep: React.FC = () => {
  const intl = useIntl();
  const { completeCurrentStep, goToNextStep } = useWizardContext();
  const [selected, setSelected] = useState<SelectedExtension | null>(null);

  const { data: appsData, loading: appsLoading } = useInstalledAppsListQuery({
    variables: {
      first: 100,
      filter: { type: AppTypeEnum.THIRDPARTY },
    },
  });

  const { data: pluginsData, loading: pluginsLoading } = usePluginsQuery({
    variables: { first: 100 },
  });

  const installedApps = mapEdgesToItems(appsData?.apps) ?? [];
  const paymentApps = installedApps.filter(app =>
    isSupportedPayment(app.name),
  );

  const allPlugins = mapEdgesToItems(pluginsData?.plugins) ?? [];
  const paymentPlugins = allPlugins.filter(plugin =>
    isSupportedPayment(plugin.name),
  );

  const extensionItems: ExtensionItem[] = [
    ...paymentApps.map(app => ({
      id: app.id,
      name: app.name ?? "",
      logoUrl: app.brand?.logo?.default ?? null,
      isActive: app.isActive ?? false,
      type: "app" as const,
    })),
    ...paymentPlugins.map(plugin => {
      const isActive =
        plugin.globalConfiguration?.active === true ||
        plugin.channelConfigurations?.some(c => c.active) === true;

      return {
        id: plugin.id,
        name: plugin.name,
        logoUrl: null,
        isActive,
        type: "plugin" as const,
      };
    }),
  ];

  const loading = appsLoading || pluginsLoading;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    completeCurrentStep();
    goToNextStep();
  };

  if (loading) {
    return (
      <StepWrapper
        title={intl.formatMessage(messages.title)}
        description={intl.formatMessage(messages.description)}
        skippable={true}
        formId={PAYMENT_FORM_ID}
        isSaving={false}
      >
        <form id={PAYMENT_FORM_ID} onSubmit={onSubmit} />
        <Box display="flex" flexDirection="column" gap={3}>
          <Skeleton __height="72px" borderRadius={4} />
          <Skeleton __height="72px" borderRadius={4} />
          <Skeleton __height="72px" borderRadius={4} />
        </Box>
      </StepWrapper>
    );
  }

  return (
    <StepWrapper
      title={intl.formatMessage(messages.title)}
      description={intl.formatMessage(messages.description)}
      skippable={true}
      formId={PAYMENT_FORM_ID}
      isSaving={false}
    >
      <form id={PAYMENT_FORM_ID} onSubmit={onSubmit} />
      {selected ? (
        selected.type === "app" ? (
          <AppIframeView
            appId={selected.id}
            onBack={() => setSelected(null)}
          />
        ) : (
          <PluginConfigView
            pluginId={selected.id}
            onBack={() => setSelected(null)}
          />
        )
      ) : extensionItems.length === 0 ? (
        <EmptyState
          icon={CreditCard}
          message={intl.formatMessage(messages.noPaymentApps)}
        />
      ) : (
        <ExtensionList
          extensions={extensionItems}
          onConfigure={item =>
            setSelected({ id: item.id, type: item.type })
          }
          emptyMessage={intl.formatMessage(messages.noPaymentApps)}
        />
      )}
    </StepWrapper>
  );
};
